import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import uuid from 'uuid';
import xhr from 'xhr';

import {
    exitFullScreen,
    areEquivalent,
    addFullScreenListeners,
    removeFullscreenListeners,
} from 'phoenix2/utils';
import { getKeyPressed, shouldKeyTrigger } from 'phoenix2/utils/keyboard';

import alertUpdate from 'imported-assets/img/alert_update@2x.png';
import spin from 'imported-assets/img/spin.svg';

const SEEK_DEBOUNCE_MS = 1000;

class VideoPlayer extends React.Component {
    static displayName = 'VideoPlayer';

    /**
     * // Video/Poster - props are passed in via a container component,
     * // VideoContainer, which connects to the redux store
     * videoId: the id of the video
     * videoUrl: the URL of the video
     * posterUrl: the URL of the image of the last frame of the video
     * subtitleUrl: the URL of the subtitle file (.vtt file)
     * videoType: can be "skill" (i.e. a video about a skill), or "tutorial" (a
     * video for teaching how to use Sparx)
     * disableSeek: If true the user can't fast forward the video.
     * corsSafe controls whether we send credentials on video load
     *
     * // Misc
     * hidePoster: if true, hides the video poster
     * skipToFinalFrame: if true, skip the video to the final frame
     *      (used in tethering where no poster is available)
     * forcePause: true when the component must ensure the video is paused
     *      and not in full screen mode
     * autoPlay: if true, the video automatically begins playing
     *
     * // Load State
     * videoLoaded: true if the video was loaded successfully
     * errorLoadingVideo: true if the video could not be loaded
     * videoLoadAttempt: the video load attempt number
     *      (e.g. 1 would be the first attempt)
     * posterLoaded: true if the poster was loaded successfully
     *      NOTE: We approximate the poster being loaded by making a HEAD
     *            request for the poster.
     * posterLoadAttempt: the poster load attempt number
     *
     * // Callbacks
     * onStart: callback that is fired
     * onPlay: ...
     * onPause: ...
     * onEnd: ...
     * onVideoLoadStart: callback fired when video mount
     * onVideoLoadSuccess: callback fired when video loads
     * onVideoLoadFail: callback fired when load attempt of video fails
     * onVideoLoadEnd: callback fired when video unmount
     * onPosterLoadStart: callback fired when poster mount
     * onPosterLoadSuccess: callback fired when poster loads
     * onPosterLoadFail: callback fired when load attempt of poster fails
     * onPosterLoadEnd: callback fired when poster unmount
     */
    static propTypes = {
        // Video/Poster
        videoId: PropTypes.string,
        videoUrl: PropTypes.string,
        posterUrl: PropTypes.string,
        subtitleUrl: PropTypes.string,
        videoType: PropTypes.string.isRequired,
        disableSeek: PropTypes.bool,
        showSubtitles: PropTypes.bool,
        corsSafe: PropTypes.bool,

        // Misc
        hidePoster: PropTypes.bool,
        skipToFinalFrame: PropTypes.bool,
        forcePause: PropTypes.bool.isRequired,
        autoPlay: PropTypes.bool,
        outroLength: PropTypes.number,

        // Load State
        videoLoaded: PropTypes.bool,
        errorLoadingVideo: PropTypes.bool,
        videoLoadAttempt: PropTypes.number,
        posterLoaded: PropTypes.bool,
        errorLoadingPoster: PropTypes.bool,
        posterLoadAttempt: PropTypes.number,

        // Styling
        style: PropTypes.object,

        // Callbacks
        onStart: PropTypes.func,
        onPlay: PropTypes.func,
        onPause: PropTypes.func,
        onSeeked: PropTypes.func,
        onFullscreen: PropTypes.func,
        onReplay: PropTypes.func,
        onEnd: PropTypes.func,
        onReachedEnd: PropTypes.func,

        onVideoLoadStart: PropTypes.func.isRequired,
        onVideoCanPlay: PropTypes.func.isRequired,
        onPosterLoadStart: PropTypes.func.isRequired,
        onVideoLoadSuccess: PropTypes.func.isRequired,
        onPosterLoadSuccess: PropTypes.func.isRequired,
        onVideoLoadFail: PropTypes.func.isRequired,
        onPosterLoadFail: PropTypes.func.isRequired,
        onVideoLoadEnd: PropTypes.func.isRequired,
        onPosterLoadEnd: PropTypes.func.isRequired,

        onVideoErrorClicked: PropTypes.func.isRequired,
    };

    static defaultProps = {
        forcePause: false,
        corsSafe: false,
    };

    constructor(props) {
        super(props);

        this.videoId = null;
        this.watchId = null;
        this.failuresReported = 0;

        // Timeout used to debounce seeked events for analytics:
        this.seekDebounceTimeout = null;
        // Last position the user seeked to in the video, in seconds
        this.lastSeek = null;

        // Remember maximum time reached in playing, for replay detection:
        this.maxTimeReached = 0;

        this.state = {
            mounted: false,
        };

        // Cache current time of video so we can tell if seeking forwards or
        // backward
        this._timeReached = 0;

        // The video is considered 'played' this many seconds before the end
        this.endOffset = 0.02;

        // Any completion callback is triggered this many seconds before the end - by
        // default it is the same as `endOffset` but mnay be overridden by the `outroLength` prop
        this.completionCallbackOffset = 0.02;
    }

    static checkPosterLoad(posterUrl) {
        return new Promise((resolve, reject) => {
            xhr.head(posterUrl, function(err, resp) {
                if (resp.statusCode === 200) {
                    resolve();
                } else {
                    reject(err);
                }
            });
        });
    }

    // Make head call to asset server to check whether the video poster is present.
    // If it isn't then trigger callbacks to notify the server.
    UNSAFE_componentWillMount() {
        const { videoId, onPosterLoadSuccess, onPosterLoadFail } = this.props;
        VideoPlayer.checkPosterLoad(this.props.posterUrl)
            .then(() => {
                onPosterLoadSuccess(videoId);
            })
            .catch(error => {
                onPosterLoadFail(videoId, false, error);
            });
    }

    componentDidMount() {
        this.watchId = uuid.v4();
        this.props.onStart(this.watchId);
        this.props.onVideoLoadStart(this.props.videoId);
        this.props.onPosterLoadStart(this.props.videoId);

        // Keyboard shortcut for Space to play/pause video. Don't fire of video
        // is focused, as in that case whatever is focused should be activated.
        this._keyListener = document.addEventListener('keydown', evt => {
            const video = this._video;
            if (getKeyPressed(evt) === ' ' && video && evt.target !== video) {
                if (video.paused) {
                    video.play();
                } else {
                    video.pause();
                }
            }
        });

        // This causes the video control bar to fade in, which fixes a bug
        // in Chrome where the control bar is the wrong size occasionally
        // due to the view animations
        this.timeoutID = setTimeout(() => this.setState({ mounted: true }), 500);

        this._fullscreenListeners = addFullScreenListeners(() => {
            if (document.fullscreenElement) {
                this.fullscreened();
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (this.props.forcePause && !prevProps.forcePause) {
            exitFullScreen();
            this._video.pause();
        }

        // Save videoId to the state so we can use it in componentWillUnmount.
        // We can't use props.videoId at this point as it has been unset.
        const videoId = this.props.videoId;
        if (videoId && this.videoId !== videoId) {
            this.videoId = videoId;
        }
    }

    componentWillUnmount() {
        const { onVideoLoadEnd, onPosterLoadEnd, onEnd } = this.props;
        const { videoId } = this.state;
        clearTimeout(this.timeoutID);
        this.setState({ mounted: false });
        onVideoLoadEnd(videoId);
        onPosterLoadEnd(videoId);
        // Send any debounced seek events (to ensure correct order):
        this.sendSeekEvent();
        const video = this._video;
        if (video) {
            onEnd(this.watchId, video.currentTime);
        } else {
            // If the video element is not present it means it has failed to
            // load and been replaced with an error.
            onEnd(this.watchId, 0, 'Problem loading video');
        }
        document.removeEventListener('keydown', this._keyListener);
        if (this._fullscreenListeners) {
            removeFullscreenListeners(this._fullscreenListeners);
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { forcePause, videoId, videoLoaded } = this.props;
        return (
            forcePause !== nextProps.forcePause ||
            videoId !== nextProps.videoId ||
            !videoLoaded ||
            this.state.mounted !== nextState.mounted ||
            !areEquivalent(nextProps.style, this.props.style)
        );
    }

    render() {
        const {
            videoId,
            videoUrl,
            posterUrl,
            subtitleUrl,
            skipToFinalFrame,
            autoPlay,
            videoLoaded,
            errorLoadingVideo,
            videoLoadAttempt,
            posterLoaded,
            errorLoadingPoster,
            style,
            showSubtitles,
            corsSafe,
            hidePoster,
            outroLength,
        } = this.props;

        if (outroLength > 0) {
            this.completionCallbackOffset = outroLength;
        }

        const { mounted } = this.state;

        let subtitleTrack = null;
        if (showSubtitles && subtitleUrl) {
            subtitleTrack = (
                <track
                    label="English"
                    kind="subtitles"
                    srcLang="en"
                    src={subtitleUrl}
                    default={true}
                />
            );
        }

        // NOTE: The key for a video includes the videoId and the attempt
        // number. The attempt number incrementing changes the key, forcing
        // the video to remount and reload.
        const video = (
            <video
                controls
                key={videoId + '_attempt_' + videoLoadAttempt}
                ref={el => (this._video = el)}
                poster={skipToFinalFrame || hidePoster ? null : posterUrl}
                src={videoUrl}
                onPlay={this.play.bind(this)}
                onPause={this.pause.bind(this)}
                onSeeked={this.seeked.bind(this)}
                onLoadedData={this.onVideoLoadSuccess.bind(this)}
                onCanPlay={this.props.onVideoCanPlay}
                onError={this.onVideoLoadError.bind(this)}
                onTimeUpdate={this.timeUpdate.bind(this)}
                onSeeking={this.seeking.bind(this)}
                crossOrigin={corsSafe ? 'use-credentials' : 'anonymous'}
                autoPlay={autoPlay}
            >
                {subtitleTrack}
            </video>
        );

        // Error to show if the video couldn't load.
        const videoError = (
            <div
                className="image-error-loading"
                tabIndex="0"
                onClick={() => this.props.onVideoErrorClicked()}
                onKeyDown={evt => shouldKeyTrigger(evt) && this.props.onVideoErrorClicked()}
                onMouseDown={evt => {
                    // Prevent focussing when clicked:
                    evt.preventDefault();
                }}
            >
                <img src={alertUpdate} />
                <div>Problem loading video</div>
            </div>
        );

        // Spinner to show whilst the video is loading.
        const spinner = (
            <div className="video-loading">
                <div className="spinner-wrapper">
                    <img className="spinner" src={spin} />
                </div>
                <div className="spinner-text">Loading Video</div>
            </div>
        );

        const showSpinner =
            !skipToFinalFrame &&
            !posterLoaded &&
            !errorLoadingPoster &&
            !videoLoaded &&
            !errorLoadingVideo;
        const showError = errorLoadingVideo;
        const classes = classnames('video', {
            mounted: mounted,
            hidden: showSpinner,
        });
        return (
            <div className="video-player" style={style}>
                {showSpinner ? spinner : null}
                <div key={'video-player'} className={classes}>
                    {showError ? videoError : video}
                </div>
            </div>
        );
    }

    onVideoLoadSuccess() {
        const video = this._video;

        // When the video has loaded, set it to the final frame if required:
        // Note: Set to slightly less than the final time, due to a bug in
        // IOS Safari:
        if (this.props.skipToFinalFrame) {
            video.currentTime = video.duration - 0.01;
        }

        // Disable downloading the video on newer browsers. Note, this is a new
        // HTML attribute not supported in current version of React, but will be
        // very soon
        video.setAttribute('controlsList', 'nodownload');

        // Mark the video as successfully loaded.
        this.props.onVideoLoadSuccess(this.props.videoId);
    }

    onVideoLoadError(event) {
        const { onVideoLoadFail, videoId } = this.props;

        // Some browsers fire onerror with no error when the poster fails to
        // load. Ignore these as we have a separate poster load test.
        const error = event.target.error;
        if (!error) {
            return;
        }

        // Ensure we don't fire onVideoLoadFail more than once per load attempt.
        // This was happening on Chrome for videos with a dodgy codec.
        if (this.failuresReported >= this.props.videoLoadAttempt) {
            return;
        }

        onVideoLoadFail(videoId, false, error);

        // Count the number of failures we have reported.
        this.failuresReported = this.failuresReported + 1;
    }

    play() {
        // If on the final frame of the video, if play is clicked, restart
        // the video: (Note: this is particularly useful when tethering
        // where the video is initially set to the final frame, rather than
        // having a final frame image. This avoids having to tap play twice.
        const video = this._video;
        let replay = false;

        const reachedEndOfVideo = video.duration - video.currentTime <= this.endOffset;
        if (reachedEndOfVideo) {
            video.currentTime = 0;
        }

        // Replay detection for analytics.
        // Fire replay event if the video has been watched to the end and then
        // there is a play event at the beginning:
        if (this.maxTimeReached < video.currentTime) {
            this.maxTimeReached = video.currentTime;
        }

        const atVideoStart = video.currentTime === 0;
        const previouslyReachedVideoEnd =
            video.duration > this.endOffset &&
            video.duration - this.maxTimeReached <= this.endOffset;

        // Send any debounced seek events (to ensure correct order):
        this.sendSeekEvent();

        if (atVideoStart && previouslyReachedVideoEnd) {
            replay = true;
            this.props.onReplay(this.watchId, video.currentTime);
        } else {
            this.props.onPlay(this.watchId, video.currentTime, replay);
        }

        // Fire callback if paused because the video reached the end
        if (reachedEndOfVideo && this.props.onReachedEnd) {
            this.props.onReachedEnd();
        }
    }

    pause() {
        const video = this._video;

        if (this.maxTimeReached < video.currentTime) {
            this.maxTimeReached = video.currentTime;
        }

        let reachedEnd;
        // If the video has not loaded then the duration can be 0 or NaN.
        if (!video.duration) {
            reachedEnd = false;
        } else {
            reachedEnd = video.duration - video.currentTime <= this.endOffset;
        }

        // Send any debounced seek events (to ensure correct order):
        this.sendSeekEvent();
        this.props.onPause(this.watchId, video.currentTime, reachedEnd);
    }

    /**
     * Fires onSeeked callback (for analytics event) when the user seeks/jumps
     * the video to a point. Debounces this to only send event after the user
     * has finished seeking for 1 second to avoid many events being sent.
     */
    seeked() {
        const video = this._video;
        const currentTime = video.currentTime;

        if (this.seekDebounceTimeout) {
            clearTimeout(this.seekDebounceTimeout);
            this.seekDebounceTimeout = null;
        }

        this.lastSeek = currentTime;
        this.seekDebounceTimeout = setTimeout(() => this.sendSeekEvent(), SEEK_DEBOUNCE_MS);
    }

    sendSeekEvent() {
        if (this.lastSeek !== null) {
            if (this.seekDebounceTimeout) {
                clearTimeout(this.seekDebounceTimeout);
                this.seekDebounceTimeout = null;
            }
            this.props.onSeeked(this.watchId, this.lastSeek);
            this.lastSeek = null;
        }
    }

    /**
     * Fires onFullscreen callback (for analytics event) when the user presses
     * fullscreen
     */
    fullscreened() {
        // Send any debounced seek events (to ensure correct order):
        this.sendSeekEvent();

        const video = this._video;
        if (video) {
            this.props.onFullscreen(this.watchId, video.currentTime);
        }
    }

    timeUpdate() {
        // Record the video current time, so that we can prevent seeking
        // forwards for tutorial videos
        const video = this._video;
        if (!video.seeking) {
            this._timeReached = Math.max(video.currentTime, this._timeReached);
        }
        // // Fire callback if the video reached the end
        const reachedEnd = video.duration - this._timeReached <= this.completionCallbackOffset;
        if (!video.seeking && reachedEnd && this.props.onReachedEnd) {
            this.props.onReachedEnd();
        }
    }

    seeking() {
        const video = this._video;
        const { disableSeek } = this.props;

        // Prevent the video seeking forward if in tutorial mode:
        if (disableSeek && this._timeReached < video.currentTime) {
            video.currentTime = this._timeReached;
        }
    }
}

export default VideoPlayer;
